import { App, URLOpenListenerEvent } from '@capacitor/app';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  inject,
  signal,
} from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';
import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular';
import {
  SpxSpinnerComponent,
  spxSpinnerActions,
  spxSpinnerReducer,
} from '@softpak/components/spx-spinner';
import { SpxStorage, SpxStorageKeyEnum } from '@softpak/components/spx-storage';
import {
  SpxToasterComponent,
  SpxToasterMessageI,
  spxToasterActions,
  spxToasterReducer,
} from '@softpak/components/spx-toaster';
import { StatusBar, Style } from '@capacitor/status-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { loadingPage, textLoading } from './shared/_config/i18n/i18n-interface';
import {
  spxChannelActions,
  spxChannelReducer,
} from '@softpak/components/spx-channel-selection';

import { AmqService } from './shared/services/activeMQ/amq-service.service';
import { AppShortcutsComponent } from './app-shortcuts.component';
import { Capacitor } from '@capacitor/core';
import { ChunkLoadRecoveryService } from './shared/services/chunk-load-recovery.service';
import { I18NLanguageMapper } from './shared/_config/i18n/i18n-lang-mapper';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { SharedAuthService } from './shared/services/shared-auth.service';
import { SpxConfirmDisplayerComponent } from '@softpak/components/spx-confirm';
import { StatsUpdateLogActions } from './store/stats-update-log/stats-update-log.actions';
import { StatsUpdateLogQueryService } from './shared/services/stats-update-log-query.service';
import { Store } from '@ngrx/store';
import { navItems } from './shared/navigation/navigation.const';
import { sharedChannelList } from './shared/_config/channels/shared-channel.const';
import { spxNavigationActions } from '@softpak/components/spx-navigation';
import { spxUpdateCheckActions } from '@softpak/components/spx-update';
import { statsVersionCheckActions } from './store/stats-version-check';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [
    IonApp,
    IonRouterOutlet,
    AppShortcutsComponent,
    TranslatePipe,
    SpxConfirmDisplayerComponent,
    SpxToasterComponent,
    SpxSpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class AppComponent {
  private readonly appStore = inject(Store);
  private readonly platform = inject(Platform);
  private readonly router = inject(Router);
  private readonly sharedAuthService = inject(SharedAuthService);
  private readonly _amqService = inject(AmqService);
  private readonly translateService = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly chunkLoadRecovery = inject(ChunkLoadRecoveryService);
  private readonly statsUpdateLogQueryService = inject(StatsUpdateLogQueryService);

  protected readonly loading = signal<boolean>(false);
  protected readonly messages = signal<SpxToasterMessageI[]>([]);
  protected readonly txtLoading = signal<string>(textLoading);

  constructor(
  ) {
    this.initializeApp();
  }

  initializeApp() {
    this.appStore.dispatch(spxChannelActions.initialize({ channels: sharedChannelList }));
    this.appStore.dispatch(spxUpdateCheckActions.initialize());
    this.appStore.dispatch(spxNavigationActions.initialize({ navigationItems: navItems }));
    this.appStore.select(spxChannelReducer.default.selectChannel).subscribe((channel) => {
      if (channel) {
        this.sharedAuthService.tryAutomaticSignIn();
      }
    });
    this.appStore.select(spxSpinnerReducer.default.selectShow).subscribe((state) => {
      this.loading.set(state);
    });
    this.appStore.select(spxSpinnerReducer.default.selectLoadActions).subscribe(actions => {
      this.txtLoading.set(actions.length >= 1 ? actions.map(a => this.translateService.instant(a)).join(', ') : this.translateService.instant(textLoading));
    });
    this.appStore.select(spxToasterReducer.default.selectMessages).subscribe((state) => {
      this.messages.set(state);
    });
    this.router.events.subscribe((routerEvent: RouterEvent | Event) => {
      this.checkRouterEvent(routerEvent);
    });
    this.chunkLoadRecovery.initialize();

    this.translateService.use(
      I18NLanguageMapper.mapLanguage(this.translateService.getBrowserLang())
    );

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.ngZone.run(() => {
        // Example url: https://truck-visit-sign-in-page.vercel.app/.....
        const url = event.url.split('?url=').pop();
        if (url) {
          this.router.navigateByUrl(url);
        }
      });
    });

    this.platform.ready().then(async () => {
      await StatusBar.setStyle({ style: Style.Light });
      await this.syncLiveUpdateState();
      await this.initVersionCheck();
    });

  }

  public onCloseMessage(id: number) {
    this.appStore.dispatch(spxToasterActions.clearMessage({ id }));
  }

  private checkRouterEvent(routerEvent: RouterEvent | Event): void {
    if (routerEvent instanceof NavigationStart) {
      this.appStore.dispatch(spxSpinnerActions.show({
        action: loadingPage
      }));
    }

    if (routerEvent instanceof NavigationEnd) {
      this.chunkLoadRecovery.clearReloadAttempt();
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.appStore.dispatch(spxSpinnerActions.hide({
        action: loadingPage
      }));
    }

    if (
      routerEvent instanceof NavigationError
    ) {
      this.chunkLoadRecovery.handleNavigationError(routerEvent);
    }
  }

  private async initVersionCheck() {
    if (Capacitor.getPlatform() !== 'web') {
      const appInfo = await App.getInfo();
      SpxStorage.setSetting(SpxStorageKeyEnum.bundleVersion, appInfo.version);

      this.appStore.dispatch(statsVersionCheckActions.load({
        query: {
          app: 'orders',
          bundleVersion: SpxStorage.getSetting(SpxStorageKeyEnum.bundleVersion),
        }
      }));
    }
  }

  private async syncLiveUpdateState(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    this.appStore.dispatch(StatsUpdateLogActions.queue({
      query: this.statsUpdateLogQueryService.createUpdateLogQuery('LIVE_UPDATE_STATE_SYNC', 'STARTED', {
        updateCheckType: 'BACKGROUND',
      }),
    }));

    try {
      const [{ bundleId }, { channel }] = await Promise.all([
        LiveUpdate.getCurrentBundle(),
        LiveUpdate.getChannel(),
      ]);

      if (bundleId) {
        SpxStorage.setSetting(SpxStorageKeyEnum.liveUpdate, bundleId);
      } else {
        SpxStorage.clearSetting(SpxStorageKeyEnum.liveUpdate);
      }

      if (channel) {
        SpxStorage.setSetting(SpxStorageKeyEnum.liveUpdateChannel, channel);
      } else {
        SpxStorage.clearSetting(SpxStorageKeyEnum.liveUpdateChannel);
      }

      this.appStore.dispatch(StatsUpdateLogActions.queue({
        query: this.statsUpdateLogQueryService.createUpdateLogQuery('LIVE_UPDATE_STATE_SYNC', 'SUCCEEDED', {
          details: {
            bundleId: bundleId ?? '',
            channel: channel ?? '',
          },
          updateCheckType: 'BACKGROUND',
        }),
      }));
    } catch (error) {
      console.warn('Unable to sync live update state.', error);
      this.appStore.dispatch(StatsUpdateLogActions.queue({
        query: this.statsUpdateLogQueryService.createUpdateLogQuery('LIVE_UPDATE_STATE_SYNC', 'FAILED', {
          error: this.getReadableErrorReason(error),
          updateCheckType: 'BACKGROUND',
        }),
      }));
    }
  }

  private stringify(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private getReadableErrorReason(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return this.stringify(error);
  }
}
